/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import { useCallback } from "react";
import { useCsvExportContext } from "../../contexts/csvExportContext";

/**
 * Thin page-facing handle for the background CSV export flow. The actual
 * lifecycle (POST, poll, download, notify) lives in CsvExportProvider — mounted
 * above the router — so the export keeps running and can download/notify even
 * after the user navigates away from the page that started it. Report-agnostic:
 * a second export button reuses it with a different `report`/`getFilters`.
 *
 * @param {object}   opts
 * @param {string}   opts.report      report key sent to the API (e.g. 'orders').
 * @param {function} opts.getFilters  called at click time; returns the filters object.
 * @param {string}  [opts.fileName]   best-effort download filename (same-origin only).
 * @param {string}  [opts.label]      module label prefixed on the success toast
 *                                    (e.g. 'Order Report'); falls back to a generic message.
 * @returns {{ startExport: function, isExporting: boolean }}
 */
export const useCsvExport = ({ report, getFilters, fileName, label } = {}) => {
  const { startExport: start, isExporting } = useCsvExportContext();

  const startExport = useCallback(() => {
    start({ report, params: getFilters?.() || {}, fileName, label });
  }, [report, getFilters, fileName, label, start]);

  return { startExport, isExporting: isExporting(report) };
};
